import React, { useState, useEffect, useCallback } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Cpu,
  TrendingUp,
  GitCompare,
  Search,
  Wrench,
  Brain,
  BarChart3,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Shield,
  Bell,
  Users,
  Database,
  Activity,
  Zap,
  Network,
  HeartPulse,
  ClipboardCheck,
  GitBranch,
  BookOpen,
  MemoryStick,
  Scale,
  MessageSquare,
  Box,
  Target,
  Eye,
  Layers,
  FlaskConical,
  Gauge,
  GraduationCap,
  Compass,
  FileText,
  Map,
  Moon,
  ShieldAlert,
  Share2,
  Video,
  Sparkles,
  BarChart2,
  GitMerge,
  Lightbulb,
  Calculator,
  Workflow,
  Globe,
  Bot,
  ThumbsUp,
  PlayCircle,
} from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '../../contexts/AuthContext'
import { useIndustry } from '../../contexts/IndustryContext'
import { isModuleEnabled, ModuleId } from '../../config/modules'
import { useDeploymentMode } from '../../hooks/useDeploymentMode'
import { useSubscription } from '../../hooks/useSubscription'
import RyedoreLogo from '../common/RyedoreLogo'
import { maskText } from '../../utils/spokeMasking'
import PinnedAssetsList from '../engagement/PinnedAssetsList'

// ─── Types ──────────────────────────────────────────────────

interface NavItem {
  path: string
  label: string | ((industry: string) => string)
  icon: React.ReactNode
  badge?: string
  permission?: string
  moduleId?: ModuleId
  hubOnly?: boolean
  industryOnly?: string[]
  /** Optional section header rendered above this item in the sidebar */
  sectionLabel?: string
  /** On Hub only, route this entry to `hubPath` instead of `path`. Used to
   *  point the Playground "AI Assistant" entry at the full Arc planner
   *  while Spoke keeps the lighter Oracle console. */
  hubPath?: string
  /** On Hub only, show `hubLabel` instead of `label`. */
  hubLabel?: string
  /** Subscription-feature key that, when present in the Spoke's license,
   *  unlocks this `hubOnly` item on the Spoke sidebar too. Mirrors
   *  HubOnlyPage's `featureKey` prop — the canonical use case is Arc:
   *  Hub-default, but Pro+ Spoke licenses with `agentic_arc` show it. */
  featureKey?: string
}

interface NavParent extends NavItem {
  children: NavItem[]
}

type NavItemOrParent = NavItem | NavParent

function isNavParent(item: NavItemOrParent): item is NavParent {
  return 'children' in item && Array.isArray((item as NavParent).children)
}

interface NavGroup {
  id: string
  label: string
  items: NavItemOrParent[]
}

interface AdminSubItem {
  path: string
  label: string
  icon: React.ReactNode
  permission?: string
  hubOnly?: boolean
}

// ─── Navigation Data ────────────────────────────────────────
//
// Groups are organized by user intent — what question does this section answer?
//
// MONITORING:   "What's happening right now across my fleet?"
//               Real-time visibility into asset health, status, side-by-side comparison.
//
// ANALYTICS:    "What will happen, why did it happen, and what should I do?"
//               Forward-looking predictions, root cause diagnostics, maintenance planning.
//               Sub-pages nest under their parent because they serve the same question:
//               - Prediction Observatory → multi-head view of the same predictions
//               - Explainability Workbench → "why did the model predict this?"
//
// AI ENGINE:    "How does the AI work and how can I improve it?"
//               Model training, knowledge capture, brain-level AI features.
//               Brain Intelligence sub-pages are tools for the same purpose:
//               understanding, auditing, and tuning the AI models.
//               Hub-only items (Self-Healing, Memory, Generative Design, Goal Planning,
//               Design Studio, Federated Learning) are guarded and hidden on Spoke.
//
// INSIGHTS:     "What does leadership need to know?"
//               Executive dashboards and AI-generated narrative reports.

const navGroups: NavGroup[] = [
  {
    id: 'agentic',
    label: 'Agentic',
    items: [
      // 2026-07-02 — Ryekronix is the customer-facing brand for the Arc
      // agentic surface. Internal routes (/arc), API paths (/api/v2/arc/*),
      // feature keys (agentic_arc), DB tables (arc_runs/arc_steps), and
      // component names (ArcConsole, ArcRouter) are UNCHANGED — this is
      // purely a UI label swap.
      { path: '/arc', label: 'Ryekronix Console', icon: <Zap size={20} />, permission: 'dashboard:read', moduleId: 'command-center', hubOnly: true, featureKey: 'agentic_arc' },
      { path: '/arc/objectives', label: 'Ryekronix Objectives', icon: <Target size={20} />, permission: 'dashboard:read', moduleId: 'command-center', hubOnly: true, featureKey: 'agentic_arc' },
      { path: '/arc/design', label: 'Ryekronix Design Studio', icon: <Layers size={20} />, permission: 'dashboard:read', moduleId: 'command-center', hubOnly: true, featureKey: 'agentic_arc' },
      { path: '/arc/experiments', label: 'Ryekronix Experiments', icon: <FlaskConical size={20} />, permission: 'dashboard:read', moduleId: 'command-center', hubOnly: true, featureKey: 'agentic_arc' },
      { path: '/arc/approvals', label: 'Ryekronix Approvals', icon: <Shield size={20} />, permission: 'dashboard:read', moduleId: 'command-center', hubOnly: true, featureKey: 'agentic_arc' },
      { path: '/arc/learning', label: 'Ryekronix Learning', icon: <TrendingUp size={20} />, permission: 'dashboard:read', moduleId: 'command-center', hubOnly: true, featureKey: 'agentic_arc' },
    ],
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    items: [
      // 2026-07-02 — Phase X sub-nav: wired 40+ page files (previously
      // implemented but never routed) as real sub-routes in each module's
      // inner Router. See modules/*/pages/*.tsx.
      {
        path: '/',
        label: 'Command Center',
        icon: <LayoutDashboard size={20} />,
        permission: 'dashboard:read',
        moduleId: 'command-center',
        children: [
          { path: '/alerts', label: 'Critical Alerts', icon: <Bell size={18} />, permission: 'dashboard:read', moduleId: 'command-center' },
          { path: '/intelligence', label: 'Fleet Intelligence', icon: <Brain size={18} />, permission: 'dashboard:read', moduleId: 'command-center' },
          { path: '/actions', label: 'Action Queue', icon: <ClipboardCheck size={18} />, permission: 'dashboard:read', moduleId: 'command-center' },
        ],
      },
      {
        path: '/assets',
        label: (industry) => industry === 'healthcare' ? 'Patient Intelligence' : 'Asset Intelligence',
        icon: <Cpu size={20} />,
        permission: 'assets:read',
        moduleId: 'asset-intelligence',
        // NOTE: children use /asset-intel/* (not /assets/*) because FastAPI
        // mounts StaticFiles at /assets — direct loads/refreshes of
        // /assets/<sub> return 404 instead of the SPA fallback.
        children: [
          { path: '/asset-intel/list', label: 'Asset List', icon: <Database size={18} />, permission: 'assets:read', moduleId: 'asset-intelligence' },
          { path: '/asset-intel/overview', label: 'Asset Overview', icon: <LayoutDashboard size={18} />, permission: 'assets:read', moduleId: 'asset-intelligence' },
          { path: '/asset-intel/health', label: 'Health & Status', icon: <HeartPulse size={18} />, permission: 'assets:read', moduleId: 'asset-intelligence' },
          { path: '/asset-intel/anomaly', label: 'Anomaly Fingerprints', icon: <Activity size={18} />, permission: 'assets:read', moduleId: 'asset-intelligence' },
          { path: '/asset-intel/ttf', label: 'Time-to-Failure', icon: <Gauge size={18} />, permission: 'assets:read', moduleId: 'asset-intelligence' },
          { path: '/asset-intel/sensor', label: 'Sensor Analysis', icon: <BarChart2 size={18} />, permission: 'assets:read', moduleId: 'asset-intelligence' },
          { path: '/asset-intel/patterns', label: 'Historical Patterns', icon: <TrendingUp size={18} />, permission: 'assets:read', moduleId: 'asset-intelligence' },
          { path: '/asset-intel/model', label: 'Model Report', icon: <FileText size={18} />, permission: 'assets:read', moduleId: 'asset-intelligence' },
          // ex-/phase8/assets — full asset-type catalog with FMECA library
          { path: '/phase8/assets', label: 'Asset Catalog & FMECA', icon: <Database size={18} />, permission: 'assets:read', moduleId: 'asset-intelligence' },
        ],
      },
      { path: '/mine-plan', label: 'Mine Plan Map', icon: <Map size={20} />, permission: 'dashboard:read', moduleId: 'command-center', industryOnly: ['mining'] },
      // ex-/phase8/industry — cross-industry KPI dashboard
      { path: '/phase8/industry', label: 'Industry Overview', icon: <Globe size={20} />, permission: 'dashboard:read', moduleId: 'command-center' },
      // Signature Pages — registry-driven operational-domain family for the
      // current industry. `/signature` (no param) resolves the industry from
      // context in SignatureFamilyPage, so this single static entry is
      // automatically industry-aware. See migrations 212–215.
      { path: '/signature', label: 'Signature Pages', icon: <Workflow size={20} />, permission: 'dashboard:read', moduleId: 'command-center' },
      {
        path: '/compare',
        label: 'Comparative Analytics',
        icon: <GitCompare size={20} />,
        permission: 'predictions:read',
        moduleId: 'compare',
        children: [
          { path: '/compare/select', label: 'Asset Selection', icon: <Cpu size={18} />, permission: 'predictions:read', moduleId: 'compare' },
          { path: '/compare/side-by-side', label: 'Side-by-Side', icon: <GitCompare size={18} />, permission: 'predictions:read', moduleId: 'compare' },
          { path: '/compare/overlay', label: 'Overlay View', icon: <Layers size={18} />, permission: 'predictions:read', moduleId: 'compare' },
          { path: '/compare/table', label: 'Table View', icon: <BarChart3 size={18} />, permission: 'predictions:read', moduleId: 'compare' },
          { path: '/compare/benchmark', label: 'Fleet Benchmark', icon: <Gauge size={18} />, permission: 'predictions:read', moduleId: 'compare' },
          { path: '/compare/insights', label: 'Insights', icon: <Lightbulb size={18} />, permission: 'predictions:read', moduleId: 'compare' },
        ],
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics & Predictions',
    items: [
      {
        path: '/predictions',
        label: 'Predictive Ops',
        icon: <TrendingUp size={20} />,
        permission: 'predictions:read',
        moduleId: 'predictive-ops',
        children: [
          { path: '/predictions/observatory', label: 'Prediction Observatory', icon: <Eye size={18} />, permission: 'predictions:read', moduleId: 'predictive-ops' },
          { path: '/predictions/explain', label: 'Explainability Workbench', icon: <Layers size={18} />, permission: 'predictions:read', moduleId: 'predictive-ops' },
        ],
      },
      {
        path: '/root-cause',
        label: 'Root Cause Analysis',
        icon: <Search size={20} />,
        permission: 'predictions:read',
        moduleId: 'root-cause',
        children: [
          { path: '/root-cause/overview', label: 'RCA Overview', icon: <LayoutDashboard size={18} />, permission: 'predictions:read', moduleId: 'root-cause' },
          { path: '/root-cause/shap', label: 'SHAP Analysis', icon: <BarChart3 size={18} />, permission: 'predictions:read', moduleId: 'root-cause' },
          { path: '/root-cause/correlations', label: 'Correlations', icon: <GitMerge size={18} />, permission: 'predictions:read', moduleId: 'root-cause' },
          { path: '/root-cause/patterns', label: 'Failure Patterns', icon: <Workflow size={18} />, permission: 'predictions:read', moduleId: 'root-cause' },
          { path: '/root-cause/library', label: 'Cause Library', icon: <BookOpen size={18} />, permission: 'predictions:read', moduleId: 'root-cause' },
          { path: '/root-cause/report', label: 'RCA Report', icon: <FileText size={18} />, permission: 'predictions:read', moduleId: 'root-cause' },
        ],
      },
      {
        path: '/maintenance',
        label: 'Maintenance Intelligence',
        icon: <Wrench size={20} />,
        permission: 'assets:read',
        moduleId: 'maintenance',
        children: [
          { path: '/maintenance/overview', label: 'Overview', icon: <LayoutDashboard size={18} />, permission: 'assets:read', moduleId: 'maintenance' },
          { path: '/maintenance/schedule', label: 'Schedule', icon: <Calculator size={18} />, permission: 'assets:read', moduleId: 'maintenance' },
          { path: '/maintenance/work-orders', label: 'Work Orders', icon: <ClipboardCheck size={18} />, permission: 'assets:read', moduleId: 'maintenance' },
          { path: '/maintenance/parts', label: 'Parts Prediction', icon: <Box size={18} />, permission: 'assets:read', moduleId: 'maintenance' },
          { path: '/maintenance/cost', label: 'Cost Calculator', icon: <Calculator size={18} />, permission: 'assets:read', moduleId: 'maintenance' },
          { path: '/maintenance/roi', label: 'ROI Analysis', icon: <TrendingUp size={18} />, permission: 'assets:read', moduleId: 'maintenance' },
          { path: '/maintenance/efficiency', label: 'Efficiency Trends', icon: <Activity size={18} />, permission: 'assets:read', moduleId: 'maintenance' },
        ],
      },
    ],
  },
  {
    id: 'ai-engine',
    label: 'AI Engine',
    items: [
      {
        path: '/learning',
        label: 'Self-Learning Engine',
        icon: <Brain size={20} />,
        permission: 'models:read',
        moduleId: 'self-learning',
        children: [
          // ── Self-Learning operations — HUB-ONLY by architecture ──────
          // These train/push adapters, run the LoRA fine-tune, update the
          // KB auto-updater, and discover new datasets. They cannot
          // function on a Spoke without Hub coordination, so we hide them
          // from the Spoke sidebar entirely instead of showing disabled
          // buttons on dead pages.
          { path: '/learning/knowledge-workbench', label: 'Knowledge Workbench', icon: <GraduationCap size={18} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true },
          { path: '/learning/lora-adapters', label: 'LoRA Adapters', icon: <Layers size={18} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true },
          { path: '/learning/adapter-sync', label: 'Adapter Sync', icon: <Share2 size={18} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true },
          { path: '/learning/continual', label: 'Continual Learning', icon: <GraduationCap size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/learning/learning-cycles', label: 'Learning Cycles', icon: <Activity size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/learning/dataset-discovery', label: 'Dataset Discovery', icon: <Database size={18} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true },
          { path: '/learning/knowledge-updates', label: 'Knowledge Updates', icon: <Globe size={18} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true },
        ],
      },
      {
        path: '/brain',
        label: 'Brain Intelligence',
        icon: <HeartPulse size={20} />,
        permission: 'models:read',
        moduleId: 'self-learning',
        children: [
          // ═══════════════════════════════════════════════════════════════
          // Phase D.5 redesign (2026-04-21)
          // Collapsed from 30 entries to 13 (+ 2 hub-only) by merging 20
          // read-only viewer pages into 4 tabbed pages (Explain / Model
          // Health / Evolution / Knowledge Browser). The 7 action-taking
          // pages stay under a "PLAYGROUND" divider so the read-vs-do
          // split is visually obvious. Legacy routes still work — they
          // are now reachable via the merged tabs' ?tab= param.
          // ═══════════════════════════════════════════════════════════════

          // ── INTELLIGENCE VIEWS (read-only drilldowns) ─────────────────
          { path: '/brain/cognitive-twin', label: 'Live Intelligence', icon: <HeartPulse size={18} />, permission: 'models:read', moduleId: 'self-learning', sectionLabel: 'INTELLIGENCE' },
          { path: '/brain/model-health-tabs', label: 'Model Health', icon: <Gauge size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/brain/explain', label: 'Explain', icon: <GitBranch size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/brain/knowledge-browser', label: 'Knowledge Browser', icon: <BookOpen size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/brain/evolution', label: 'Evolution', icon: <Workflow size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/brain/video-analysis', label: 'Visual Inspection', icon: <Video size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/brain/goals', label: 'Objective Alignment', icon: <Target size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/brain/kpis', label: 'Brain Scorecard', icon: <BarChart3 size={18} />, permission: 'models:read', moduleId: 'self-learning' },

          // ── PLAYGROUND (action-taking pages — "do", not "look") ───────
          // PLAYGROUND was Oracle on Spoke + Arc on Hub. After the May 3
          // 2026 consolidation, both surfaces are the Arc Console — the
          // dedicated AGENTIC section (`/arc`) is the canonical entry.
          // Removing the duplicate /oracle and /arc 'Ask the AI' entries
          // from AI Engine to avoid 4 sidebar items pointing at /arc.
          { path: '/brain/twin', label: 'Digital Twin Lab', icon: <Box size={18} />, permission: 'models:read', moduleId: 'self-learning', sectionLabel: 'PLAYGROUND' },
          { path: '/brain/physics', label: 'Physics Lab', icon: <FlaskConical size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/brain/hypothesis-lab', label: 'Hypothesis Generator', icon: <Lightbulb size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          // Renamed from "Experiment Lab" to "ML Training Lab" to
          // disambiguate from /arc/experiments (operational what-if).
          { path: '/brain/experiment-lab', label: 'ML Training Lab', icon: <Sparkles size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          // /brain/design-studio dropped — same backend as /arc/design,
          // which is the canonical Design Studio entry under AGENTIC.
          { path: '/brain/decision-optimizer', label: 'Smart Actions', icon: <Calculator size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/brain/artifacts', label: 'Workspace', icon: <Users size={18} />, permission: 'models:read', moduleId: 'self-learning' },

          // ── Hub-only — not visible on Spoke deployments ──────────────
          { path: '/brain/agent-teams', label: 'Agentic AI', icon: <Bot size={18} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true, sectionLabel: 'HUB ONLY' },
          { path: '/brain/industry-discovery', label: 'New Industry Detector', icon: <Globe size={18} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true },
        ],
      },
      // Hub-only — coordinates multiple Spoke participants
      { path: '/federated', label: 'Federated Learning', icon: <Network size={20} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true },
      // ex-"Phase 8" surfaces — advanced analyzer + cognitive panels
      {
        path: '/phase8/cognitive',
        label: 'Advanced Intelligence',
        icon: <Sparkles size={20} />,
        permission: 'models:read',
        moduleId: 'self-learning',
        children: [
          { path: '/phase8/cognitive', label: 'Cognitive Assistant', icon: <Brain size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/phase8/wow', label: 'Insight Analyzers', icon: <Sparkles size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/phase8/deep-analyzers', label: 'Deep Analyzers', icon: <Cpu size={18} />, permission: 'models:read', moduleId: 'self-learning' },
          { path: '/phase8/xr', label: 'Mixed Reality (AR/VR)', icon: <Network size={18} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true },
          { path: '/phase8/meta-learning', label: 'Meta-Learning', icon: <GitBranch size={18} />, permission: 'models:read', moduleId: 'self-learning', hubOnly: true },
        ],
      },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    items: [
      {
        path: '/executive',
        label: 'Executive Insights',
        icon: <BarChart3 size={20} />,
        permission: 'reports:read',
        moduleId: 'executive',
        children: [
          { path: '/executive/summary', label: 'Executive Summary', icon: <LayoutDashboard size={18} />, permission: 'reports:read', moduleId: 'executive' },
          { path: '/executive/kpi-scorecard', label: 'KPI Scorecard', icon: <Gauge size={18} />, permission: 'reports:read', moduleId: 'executive' },
          { path: '/executive/okr', label: 'OKR Dashboard', icon: <Target size={18} />, permission: 'reports:read', moduleId: 'executive' },
          { path: '/executive/trends', label: 'Trend Analysis', icon: <TrendingUp size={18} />, permission: 'reports:read', moduleId: 'executive' },
          { path: '/executive/roi', label: 'ROI & Value', icon: <Calculator size={18} />, permission: 'reports:read', moduleId: 'executive' },
          { path: '/executive/strategic', label: 'Strategic Insights', icon: <Lightbulb size={18} />, permission: 'reports:read', moduleId: 'executive' },
          { path: '/executive/reports', label: 'Report Generator', icon: <FileText size={18} />, permission: 'reports:read', moduleId: 'executive' },
          { path: '/executive/narratives', label: 'Narrative Hub', icon: <FileText size={18} />, permission: 'reports:read', moduleId: 'executive' },
        ],
      },
      // ex-/phase8/trust — audit-chain + compliance posture (hub-only)
      { path: '/phase8/trust', label: 'Trust & Compliance', icon: <Shield size={20} />, permission: 'reports:read', moduleId: 'executive', hubOnly: true },
    ],
  },
  // 2026-07-02 — the former "Phase 8 — New Surfaces" group is GONE.
  // Internal phase jargon leaked into customer-facing nav and, because the
  // group was ungated, it was the ONLY thing shown for industries missing
  // from the module matrix. Items redistributed:
  //   /phase8/industry        → Monitoring › Industry Overview
  //   /phase8/assets          → Monitoring › Asset Intel › Asset Catalog
  //   /phase8/wow + deep + cognitive + xr + meta-learning
  //                           → AI Engine › Advanced Intelligence
  //   /phase8/trust           → Insights › Trust & Compliance (hub-only)
  //   /phase8/preferences     → bottom nav › Preferences
  //   /phase8/passkey/register→ Admin › Passkeys (hub-only)
  // Routes themselves are unchanged (/phase8/* URLs still work).
]

const bottomNavItems: NavItem[] = [
  { path: '/notifications', label: 'My Alerts', icon: <Bell size={20} /> },
  // ex-/phase8/preferences — theme + locale personalization
  { path: '/phase8/preferences', label: 'Preferences', icon: <Eye size={20} /> },
  { path: '/settings', label: 'Settings', icon: <Settings size={20} /> },
  // 2026-05-24: 'Why Ryedore' (was here at /about/capabilities) removed
  // per user request — it was a marketing surface that didn't belong
  // in the operational UI for either Hub (vendor staff) or Spoke
  // (customer workflows). The CapabilitiesPage route still exists if
  // anyone hits the URL directly.
  { path: '/help', label: 'Help', icon: <HelpCircle size={20} /> },
]

const adminSubItems: AdminSubItem[] = [
  { path: '/admin', label: 'Overview', icon: <LayoutDashboard size={18} />, permission: 'admin:access' },
  { path: '/admin/users', label: 'Users', icon: <Users size={18} />, permission: 'users:read' },
  { path: '/admin/roles', label: 'Roles & Permissions', icon: <Shield size={18} />, permission: 'roles:read' },
  { path: '/admin/notifications', label: 'Notifications', icon: <Bell size={18} />, permission: 'notifications:manage' },
  { path: '/admin/connectors', label: 'Connectors', icon: <Database size={18} />, permission: 'connectors:manage' },
  { path: '/admin/kpis', label: 'KPI Studio', icon: <Sparkles size={18} />, permission: 'admin:access' },
  { path: '/admin/usage', label: 'API Usage', icon: <Zap size={18} />, permission: 'admin:access', hubOnly: true },
  { path: '/admin/settings', label: 'System', icon: <Settings size={18} />, permission: 'admin:settings' },
  { path: '/admin/audit', label: 'Audit Log', icon: <Activity size={18} />, permission: 'admin:access', hubOnly: true },
  // Phase-2 workflow dashboards. Gated by permission('admin:access') so
  // supervisor+ roles see them; backend ALSO gates the underlying APIs.
  { path: '/admin/feedback', label: 'Feedback', icon: <ThumbsUp size={18} />, permission: 'admin:access' },
  { path: '/admin/journey', label: 'Session Replay', icon: <PlayCircle size={18} />, permission: 'admin:access' },
  { path: '/admin/learning-runs', label: 'Model Trajectory', icon: <TrendingUp size={18} />, permission: 'admin:access' },
  { path: '/admin/kb-coverage', label: 'KB Coverage', icon: <Database size={18} />, permission: 'admin:access' },
  // ex-/phase8/passkey/register — WebAuthn enrolment (hub-only)
  { path: '/phase8/passkey/register', label: 'Passkeys', icon: <Shield size={18} />, permission: 'admin:access', hubOnly: true },
]

// ─── Storage Keys ───────────────────────────────────────────

const SIDEBAR_COLLAPSED_KEY = 'ryedore-sidebar-collapsed'
const ADMIN_EXPANDED_KEY = 'ryedore-admin-expanded'
const NAV_EXPANDED_KEY = 'ryedore-nav-expanded'

// ─── Component ──────────────────────────────────────────────

export default function Sidebar() {
  const location = useLocation()
  const { hasPermission } = useAuth()
  const { industryId } = useIndustry()
  const { isHub } = useDeploymentMode()
  const { isFeatureEnabled } = useSubscription()

  // Real asset count via /fleet/health (lightweight — returns
  // total_assets directly, no per-asset payload). The previous
  // /assets?limit=1 polled a route that doesn't exist on this Hub
  // build → 404 storm in DevTools every 60s.
  const [assetCount, setAssetCount] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    const fetchCount = async () => {
      try {
        const { default: api } = await import('@/services/api')
        const { data } = await api.get<any>('/fleet/health')
        if (cancelled) return
        const total = (data?.total_assets as number)
          ?? (data?.total_in_registry as number)
          ?? null
        if (typeof total === 'number') setAssetCount(total)
      } catch {
        if (!cancelled) setAssetCount(null)
      }
    }
    fetchCount()
    const t = setInterval(fetchCount, 60_000)
    return () => { cancelled = true; clearInterval(t) }
  }, [])

  // Sidebar collapsed state
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    } catch {
      return false
    }
  })

  // Admin panel expanded state
  const [adminExpanded, setAdminExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(ADMIN_EXPANDED_KEY)
      if (stored === null) {
        return hasPermission('admin:access') || location.pathname.startsWith('/admin')
      }
      return stored === 'true' || location.pathname.startsWith('/admin')
    } catch {
      return hasPermission('admin:access') || location.pathname.startsWith('/admin')
    }
  })

  // Collapsible parent expanded states (keyed by parent path)
  const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(NAV_EXPANDED_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  // ─── Persistence ────────────────────────────────────────

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed)) } catch { /* ignore */ }
  }, [isCollapsed])

  useEffect(() => {
    try { localStorage.setItem(ADMIN_EXPANDED_KEY, String(adminExpanded)) } catch { /* ignore */ }
  }, [adminExpanded])

  useEffect(() => {
    try { localStorage.setItem(NAV_EXPANDED_KEY, JSON.stringify(expandedParents)) } catch { /* ignore */ }
  }, [expandedParents])

  // ─── Auto-expand parent when navigating to child ────────

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      setAdminExpanded(true)
    }

    // Auto-expand parent groups when navigating to their children
    const parentPaths = ['/predictions', '/learning', '/brain', '/executive']
    for (const pp of parentPaths) {
      if (location.pathname.startsWith(pp + '/') || (pp === '/brain' && location.pathname === '/brain')) {
        setExpandedParents(prev => {
          if (prev[pp]) return prev
          return { ...prev, [pp]: true }
        })
      }
    }
  }, [location.pathname])

  // ─── Helpers ────────────────────────────────────────────

  const toggleCollapsed = () => setIsCollapsed(!isCollapsed)

  const toggleAdmin = () => {
    const newState = !adminExpanded
    setAdminExpanded(newState)
    if (newState) {
      setTimeout(() => {
        document.getElementById('admin-nav-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }

  const toggleParent = useCallback((path: string) => {
    setExpandedParents(prev => ({ ...prev, [path]: !prev[path] }))
  }, [])

  const isItemVisible = useCallback((item: NavItem): boolean => {
    // Hub-only items are hidden on Spoke EXCEPT when the item declares a
    // `featureKey` and the Spoke's license enables that feature (Pro+ tier
    // unlocking Arc is the canonical case). The backend gate is still
    // authoritative — this just stops a false UI-block for paid Spokes.
    if (item.hubOnly && !isHub) {
      const tierUnlocked = item.featureKey ? isFeatureEnabled(item.featureKey) : false
      if (!tierUnlocked) return false
    }
    if (item.industryOnly && !item.industryOnly.includes(industryId)) return false
    if (item.moduleId && !isModuleEnabled(item.moduleId, industryId)) return false
    if (item.permission && !hasPermission(item.permission)) return false
    return true
  }, [isHub, industryId, hasPermission, isFeatureEnabled])

  const getLabel = (item: NavItem): string => {
    // On Hub, prefer item.hubLabel when set (used by the Playground front
    // door to surface "Arc Console" instead of "Oracle").
    const raw = isHub && item.hubLabel
      ? item.hubLabel
      : (typeof item.label === 'function' ? item.label(industryId) : item.label)
    // On Spoke, run every nav label through the IP mask so architecture
    // terminology never leaks into the left-hand navigation.
    return maskText(raw, !isHub)
  }

  // On Hub, swap path → hubPath for items that have a Hub-specific route.
  const getPath = (item: NavItem): string => {
    return isHub && item.hubPath ? item.hubPath : item.path
  }

  const isPathActive = (path: string): boolean => {
    if (path === '/') return location.pathname === '/'
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const filteredAdminSubItems = adminSubItems.filter(
    item => (!item.permission || hasPermission(item.permission)) && (!item.hubOnly || isHub)
  )

  const isAdminActive = location.pathname.startsWith('/admin')

  // ─── Render Helpers ─────────────────────────────────────

  const renderNavLink = (item: NavItem, indent = false) => {
    const label = getLabel(item)
    const destPath = getPath(item)
    return (
      <NavLink
        key={destPath}
        to={destPath}
        end={destPath === '/'}
        title={isCollapsed ? label : undefined}
        className={({ isActive }) =>
          clsx(
            'flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200',
            isCollapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2',
            indent && !isCollapsed && 'py-1.5',
            isActive
              ? 'bg-primary-600/20 text-primary-400'
              : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
          )
        }
      >
        <span className="flex-shrink-0">{item.icon}</span>
        {!isCollapsed && (
          <>
            <span className="truncate">{label}</span>
            {item.badge && (
              <span className="ml-auto badge badge-info">{item.badge}</span>
            )}
          </>
        )}
      </NavLink>
    )
  }

  const renderParentItem = (parent: NavParent) => {
    const label = getLabel(parent)
    const isExpanded = !!expandedParents[parent.path]
    const isActive = isPathActive(parent.path)

    // Filter visible children
    const visibleChildren = parent.children.filter(isItemVisible)
    if (visibleChildren.length === 0 && !isItemVisible(parent)) return null

    return (
      <div key={parent.path}>
        {/* Parent button — clickable to navigate AND toggle */}
        <div className="flex items-center">
          <NavLink
            to={parent.path}
            end
            title={isCollapsed ? label : undefined}
            className={clsx(
              'flex-1 flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200',
              isCollapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2',
              isActive
                ? 'bg-primary-600/20 text-primary-400'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
            )}
          >
            <span className="flex-shrink-0">{parent.icon}</span>
            {!isCollapsed && <span className="truncate flex-1">{label}</span>}
          </NavLink>
          {!isCollapsed && visibleChildren.length > 0 && (
            <button
              onClick={() => toggleParent(parent.path)}
              className="p-1.5 rounded text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              <ChevronDown
                size={14}
                className={clsx(
                  'transition-transform duration-200',
                  isExpanded && 'rotate-180'
                )}
              />
            </button>
          )}
        </div>

        {/* Children — collapsible */}
        {!isCollapsed && visibleChildren.length > 0 && (
          <div
            className={clsx(
              'overflow-hidden transition-all duration-300 ease-in-out',
              isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            )}
          >
            <div className="ml-4 pl-3 border-l border-surface-700/50 py-1">
              {visibleChildren.map(child => (
                <React.Fragment key={child.path}>
                  {child.sectionLabel && (
                    <div className="px-3 pt-2.5 pb-1">
                      <span className="text-[9px] uppercase tracking-widest font-bold text-primary-500/70">
                        {child.sectionLabel}
                      </span>
                    </div>
                  )}
                  {renderNavLink(child, true)}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ─── Main Render ────────────────────────────────────────

  return (
    <aside
      className={clsx(
        'bg-surface-900 border-r border-surface-700 flex flex-col transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo — Ryedore wordmark. Auto-scales between collapsed (icon
          only) and expanded (icon + product line) states. */}
      <div className="h-16 flex items-center px-4 border-b border-surface-700">
        <div className="flex items-center gap-3">
          <RyedoreLogo size={36} className="flex-shrink-0" />
          {!isCollapsed && (
            <div className="overflow-hidden">
              <h1 className="text-surface-100 font-semibold text-sm whitespace-nowrap">Ryedore</h1>
              <p className="text-surface-500 text-xs whitespace-nowrap">
                {industryId === 'healthcare' ? 'Clinical AI'
                  : industryId === 'iot_telecom' ? 'IoT Intelligence'
                  : 'Asset Intelligence'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Grouped Navigation */}
      <nav className="flex-1 py-2 px-2 overflow-y-auto">
        {/* Pinned assets + investigations roll-up (engagement §11.B2).
            Hides itself when no items are pinned so the sidebar stays quiet. */}
        {!isCollapsed && <PinnedAssetsList />}
        {navGroups.map((group) => {
          // Filter items by visibility
          const visibleItems = group.items.filter(item => {
            if (!isItemVisible(item)) return false
            // For parents, also check if parent OR any child is visible
            if (isNavParent(item)) {
              return isItemVisible(item) || item.children.some(isItemVisible)
            }
            return true
          })

          if (visibleItems.length === 0) return null

          return (
            <div key={group.id} className="mb-2">
              {/* Section Header */}
              {!isCollapsed && (
                <div className="px-3 pt-3 pb-1.5">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-surface-600">
                    {maskText(group.label, !isHub)}
                  </span>
                </div>
              )}
              {isCollapsed && (
                <div className="my-2 mx-2 border-t border-surface-700/50" />
              )}

              {/* Items */}
              <div className="space-y-0.5">
                {visibleItems.map(item =>
                  isNavParent(item) ? renderParentItem(item) : renderNavLink(item)
                )}
              </div>
            </div>
          )
        })}
      </nav>

      {/* Admin Navigation */}
      {hasPermission('admin:access') && filteredAdminSubItems.length > 0 && (
        <div id="admin-nav-section" className="py-2 px-2 border-t border-surface-700">
          <button
            onClick={toggleAdmin}
            title={isCollapsed ? 'Admin Panel' : undefined}
            className={clsx(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
              isCollapsed && 'justify-center px-2',
              isAdminActive
                ? 'bg-amber-500/20 text-amber-400'
                : 'text-amber-500 hover:text-amber-400 hover:bg-surface-800'
            )}
          >
            <Shield size={20} className="flex-shrink-0" />
            {!isCollapsed && (
              <>
                <span className="truncate flex-1 text-left">Admin Panel</span>
                {!adminExpanded && filteredAdminSubItems.length > 0 && (
                  <span className="flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-amber-500/30 text-amber-400 animate-pulse">
                    {filteredAdminSubItems.length}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={clsx(
                    'transition-transform duration-200 flex-shrink-0',
                    adminExpanded && 'rotate-180'
                  )}
                />
              </>
            )}
          </button>

          <div
            className={clsx(
              'overflow-hidden transition-all duration-300 ease-in-out',
              adminExpanded && !isCollapsed ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            )}
          >
            <div className="mt-1 ml-2 pl-4 border-l border-surface-700 space-y-0.5 py-1">
              {filteredAdminSubItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/admin'}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all duration-200',
                      isActive
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'text-surface-400 hover:text-amber-400 hover:bg-surface-800'
                    )
                  }
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>

          {isCollapsed && adminExpanded && (
            <div className="mt-1 space-y-0.5">
              {filteredAdminSubItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/admin'}
                  title={item.label}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center justify-center p-2 rounded-lg text-sm transition-all duration-200',
                      isActive
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'text-surface-400 hover:text-amber-400 hover:bg-surface-800'
                    )
                  }
                >
                  {item.icon}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="py-4 px-2 border-t border-surface-700">
        <div className="space-y-1">
          {bottomNavItems
            .filter(item => !item.permission || hasPermission(item.permission))
            .map((item) => {
              const label = getLabel(item)
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  title={isCollapsed ? label : undefined}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                      isCollapsed && 'justify-center px-2',
                      isActive
                        ? 'bg-surface-800 text-surface-200'
                        : 'text-surface-500 hover:text-surface-300 hover:bg-surface-800'
                    )
                  }
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!isCollapsed && <span className="truncate">{label}</span>}
                </NavLink>
              )
            })}
        </div>
      </div>

      {/* Sidebar Controls */}
      <div className="px-2 py-3 border-t border-surface-700">
        <button
          onClick={toggleCollapsed}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          {!isCollapsed && <span className="text-xs">Collapse</span>}
        </button>
        {!isCollapsed && (
          <p className="text-xs text-surface-600 text-center mt-2">
            v2.0.0 | {assetCount != null ? `${assetCount} Asset${assetCount === 1 ? '' : 's'}` : '— Assets'}
          </p>
        )}
      </div>
    </aside>
  )
}
